import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Home, Ticket, Megaphone, CheckCircle, ExternalLink, AlertCircle, Loader2, Lock, ArrowLeft, Send, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useCachedAvatar } from "@/hooks/useCachedAvatar";
import { useStatusPage } from "@/hooks/useStatusPage";
import { useSubscription } from "@/hooks/useSubscription";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { Drawer, DrawerContent } from "@/components/ui/drawer";

type Tab = "home" | "tickets" | "news";

interface SelectedTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
}

// Format ticket status to professional display text
const formatTicketStatus = (status: string | undefined): string => {
  if (!status) return 'Submitted';
  const statusMap: Record<string, string> = {
    'submitted': 'Submitted',
    'in_progress': 'In Progress',
    'waiting_customer': 'Awaiting Response',
    'resolved': 'Resolved',
    'closed': 'Closed',
  };
  return statusMap[status] || status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

interface MessengerWidgetProps {
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

export const MessengerWidget = ({ externalOpen, onExternalOpenChange }: MessengerWidgetProps = {}) => {
  const location = useLocation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isMobile = useIsMobile();
  
  // Hide messenger on public form pages and AI page
  const isPublicFormPage = location.pathname.startsWith('/forms/');
  const isAIPage = location.pathname === '/dashboard/quinn';
  
  // Use external control if provided, otherwise use internal state
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (open: boolean) => {
    if (onExternalOpenChange) {
      onExternalOpenChange(open);
    }
    setInternalOpen(open);
  };
  const [activeTab, setActiveTab] = useState<Tab>("home");
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketCategory, setTicketCategory] = useState("");
  const [ticketPriority, setTicketPriority] = useState("medium");
  const [ticketMessage, setTicketMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SelectedTicket | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { user } = useAuth();
  const { avatarUrl } = useCachedAvatar();
  const { data: statusData, isLoading: statusLoading, isError } = useStatusPage("status.queueos.com");
  const { subscriptionData, loading: subscriptionLoading, refetch: refetchSubscription } = useSubscription();
  const queryClient = useQueryClient();

  // Refetch subscription when widget opens to ensure fresh data
  useEffect(() => {
    if (isOpen && user) {
      refetchSubscription();
    }
  }, [isOpen, user, refetchSubscription]);

  // Check if user has a paid plan - just check if subscribed is true
  const hasPaidPlan = subscriptionData?.subscribed === true;

  // Fetch ticket categories
  const { data: ticketCategories } = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_categories')
        .select('id, name')
        .eq('is_active', true)
        .order('position')
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: hasPaidPlan && !!user,
    staleTime: 10 * 60 * 1000,
  });

  // Fetch unread message counts per ticket
  const { data: unreadCounts } = useQuery({
    queryKey: ['unread-message-counts', user?.id],
    queryFn: async () => {
      if (!user?.id) return {};
      
      // Get all user's tickets
      const { data: tickets, error: ticketsError } = await supabase
        .from('support_tickets')
        .select('id')
        .eq('user_id', user.id);
      
      if (ticketsError) throw ticketsError;
      if (!tickets?.length) return {};
      
      const ticketIds = tickets.map(t => t.id);
      
      // Get all staff messages for user's tickets (messages from staff that user might not have read)
      const { data: staffMessages, error: messagesError } = await supabase
        .from('ticket_messages')
        .select('id, ticket_id')
        .in('ticket_id', ticketIds)
        .eq('sender_type', 'staff')
        .eq('is_internal_note', false);
      
      if (messagesError) throw messagesError;
      if (!staffMessages?.length) return {};
      
      // Get read message IDs for this user
      const { data: readMessages, error: readError } = await supabase
        .from('ticket_message_reads')
        .select('message_id')
        .eq('user_id', user.id);
      
      if (readError) throw readError;
      
      const readMessageIds = new Set(readMessages?.map(r => r.message_id) || []);
      
      // Count unread messages per ticket
      const counts: Record<string, number> = {};
      staffMessages.forEach(msg => {
        if (!readMessageIds.has(msg.id)) {
          counts[msg.ticket_id] = (counts[msg.ticket_id] || 0) + 1;
        }
      });
      
      return counts;
    },
    enabled: !!user && hasPaidPlan,
    staleTime: 30 * 1000,
  });

  // Calculate total unread count
  const totalUnreadCount = Object.values(unreadCounts || {}).reduce((sum, count) => sum + count, 0);

  // Mark messages as read when viewing a ticket
  const markMessagesAsRead = useCallback(async (ticketId: string) => {
    if (!user?.id) return;
    
    try {
      // Get all staff messages for this ticket that aren't read yet
      const { data: staffMessages, error: messagesError } = await supabase
        .from('ticket_messages')
        .select('id')
        .eq('ticket_id', ticketId)
        .eq('sender_type', 'staff')
        .eq('is_internal_note', false);
      
      if (messagesError) throw messagesError;
      if (!staffMessages?.length) return;
      
      // Get already read messages
      const { data: readMessages } = await supabase
        .from('ticket_message_reads')
        .select('message_id')
        .eq('user_id', user.id)
        .eq('ticket_id', ticketId);
      
      const readMessageIds = new Set(readMessages?.map(r => r.message_id) || []);
      
      // Filter to unread messages
      const unreadMessages = staffMessages.filter(m => !readMessageIds.has(m.id));
      
      if (unreadMessages.length === 0) return;
      
      // Insert read records
      const readRecords = unreadMessages.map(msg => ({
        message_id: msg.id,
        ticket_id: ticketId,
        user_id: user.id,
      }));
      
      const { error: insertError } = await supabase
        .from('ticket_message_reads')
        .insert(readRecords);
      
      if (insertError) {
        // Ignore duplicate key errors
        if (!insertError.message?.includes('duplicate')) {
          console.error('Error marking messages as read:', insertError);
        }
      }
      
      // Invalidate unread counts query
      queryClient.invalidateQueries({ queryKey: ['unread-message-counts', user.id] });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [user?.id, queryClient]);

  // Mark messages as read when selecting a ticket
  useEffect(() => {
    if (selectedTicket?.id && user?.id) {
      markMessagesAsRead(selectedTicket.id);
    }
  }, [selectedTicket?.id, user?.id, markMessagesAsRead]);
  
  const handleSubmitTicket = async () => {
    if (!user || !ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Create the ticket
      const { data: ticket, error: ticketError } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          subject: ticketSubject.trim(),
          category_id: ticketCategory || null,
          priority: ticketPriority,
          status: 'submitted',
          source: 'dashboard'
        })
        .select('id, ticket_number')
        .single();
      
      if (ticketError) throw ticketError;
      
      // Create the initial message
      const { error: messageError } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: ticket.id,
          sender_id: user.id,
          sender_type: 'customer',
          message: ticketMessage.trim()
        });
      
      if (messageError) {
        console.error('Error creating ticket message:', messageError);
        // Don't throw - ticket was created successfully
      }
      
      toast.success(`Ticket ${ticket.ticket_number} created successfully`);
      
      // Reset form
      setTicketSubject("");
      setTicketCategory("");
      setTicketPriority("medium");
      setTicketMessage("");
      setShowNewTicketForm(false);
      
      // Refresh tickets list
      queryClient.invalidateQueries({ queryKey: ['user-tickets', user.id] });
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      toast.error(error.message || "Failed to create ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch user tickets from Supabase (only if logged in and has paid plan)
  const { data: userTickets, isLoading: ticketsLoading } = useQuery({
    queryKey: ['user-tickets', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('id, ticket_number, subject, status, priority, created_at')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
    enabled: !!user && hasPaidPlan,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch messages for selected ticket (including system messages)
  const { data: ticketMessages, isLoading: messagesLoading } = useQuery({
    queryKey: ['ticket-messages', selectedTicket?.id],
    queryFn: async () => {
      // Fetch regular messages
      const { data: regularMessages, error: messagesError } = await supabase
        .from('ticket_messages')
        .select('id, message, sender_type, sender_id, created_at, is_internal_note')
        .eq('ticket_id', selectedTicket?.id)
        .eq('is_internal_note', false);
      
      if (messagesError) throw messagesError;
      
      // Fetch system messages (non-internal notes only)
      const { data: systemMessages, error: systemError } = await supabase
        .from('ticket_system_messages')
        .select('id, message, created_at, is_internal_note')
        .eq('ticket_id', selectedTicket?.id)
        .eq('is_internal_note', false);
      
      if (systemError) throw systemError;
      
      // Fetch sender profiles for staff messages
      const staffSenderIds = [...new Set(
        regularMessages?.filter(m => m.sender_type === 'staff').map(m => m.sender_id) || []
      )];
      
      let profilesMap: Record<string, string> = {};
      if (staffSenderIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name, email')
          .in('user_id', staffSenderIds);
        
        profiles?.forEach(p => {
          profilesMap[p.user_id] = p.display_name || p.email?.split('@')[0] || 'Support';
        });
      }
      
      // Transform regular messages
      const transformedRegular = regularMessages?.map(msg => ({
        ...msg,
        sender_name: msg.sender_type === 'staff' ? (profilesMap[msg.sender_id] || 'Support') : null
      })) || [];
      
      // Transform system messages
      const transformedSystem = systemMessages?.map(msg => ({
        id: msg.id,
        message: msg.message,
        sender_type: 'system' as const,
        sender_id: null,
        created_at: msg.created_at,
        is_internal_note: msg.is_internal_note,
        sender_name: null
      })) || [];
      
      // Merge and sort by created_at
      const allMessages = [...transformedRegular, ...transformedSystem]
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      
      return allMessages;
    },
    enabled: !!selectedTicket?.id,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Scroll to bottom when messages load or change
  useEffect(() => {
    if (ticketMessages && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticketMessages]);

  // Real-time subscription for new messages and system events
  useEffect(() => {
    if (!selectedTicket?.id) return;

    console.log('Setting up real-time subscriptions for ticket:', selectedTicket.id);

    const channel = supabase
      .channel(`ticket-messages-${selectedTicket.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
          filter: `ticket_id=eq.${selectedTicket.id}`
        },
        (payload) => {
          console.log('New ticket message received:', payload);
          queryClient.invalidateQueries({ queryKey: ['ticket-messages', selectedTicket.id] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_system_messages',
          filter: `ticket_id=eq.${selectedTicket.id}`
        },
        (payload) => {
          console.log('New system message received:', payload);
          queryClient.invalidateQueries({ queryKey: ['ticket-messages', selectedTicket.id] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'support_tickets',
          filter: `id=eq.${selectedTicket.id}`
        },
        (payload) => {
          console.log('Ticket updated:', payload);
          const newStatus = (payload.new as any).status;
          if (newStatus) {
            setSelectedTicket(prev => prev && prev.status !== newStatus ? { ...prev, status: newStatus } : prev);
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up real-time subscriptions');
      supabase.removeChannel(channel);
    };
  }, [selectedTicket?.id, queryClient]);

  // Real-time subscription for new messages across all tickets (for unread counts)
  useEffect(() => {
    if (!user?.id || !hasPaidPlan) return;

    const channel = supabase
      .channel('unread-messages-global')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages'
        },
        (payload) => {
          const newMessage = payload.new as any;
          // Only care about staff messages (those we track as unread)
          if (newMessage.sender_type === 'staff' && !newMessage.is_internal_note) {
            // Invalidate unread counts to refresh
            queryClient.invalidateQueries({ queryKey: ['unread-message-counts', user.id] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, hasPaidPlan, queryClient]);

  // Presence tracking for when customer is viewing a ticket
  const presenceChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!selectedTicket?.id || !user?.id) {
      // Clean up presence when no ticket selected
      if (presenceChannelRef.current) {
        supabase.removeChannel(presenceChannelRef.current);
        presenceChannelRef.current = null;
      }
      return;
    }

    console.log('Setting up presence tracking for ticket:', selectedTicket.id);

    const presenceChannel = supabase.channel(`customer-presence-${selectedTicket.id}`);
    presenceChannelRef.current = presenceChannel;

    presenceChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await presenceChannel.track({ 
          user_id: user.id, 
          is_typing: false,
          viewing_since: new Date().toISOString()
        });
        console.log('Customer presence tracked for ticket:', selectedTicket.id);
      }
    });

    return () => {
      console.log('Cleaning up presence tracking');
      if (presenceChannelRef.current) {
        supabase.removeChannel(presenceChannelRef.current);
        presenceChannelRef.current = null;
      }
    };
  }, [selectedTicket?.id, user?.id]);

  // Track typing status
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleReplyChange = (value: string) => {
    setReplyMessage(value);

    // Update typing status
    if (presenceChannelRef.current && user?.id) {
      presenceChannelRef.current.track({ 
        user_id: user.id, 
        is_typing: true,
        viewing_since: new Date().toISOString()
      });

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set typing to false after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (presenceChannelRef.current) {
          presenceChannelRef.current.track({ 
            user_id: user.id, 
            is_typing: false,
            viewing_since: new Date().toISOString()
          });
        }
      }, 2000);
    }
  };

  const handleSendReply = async () => {
    if (!user || !selectedTicket || !replyMessage.trim()) return;
    
    setIsSendingReply(true);
    try {
      const { error } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: selectedTicket.id,
          sender_id: user.id,
          sender_type: 'customer',
          message: replyMessage.trim()
        });
      
      if (error) throw error;
      
      setReplyMessage("");
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', selectedTicket.id] });
    } catch (error: any) {
      console.error('Error sending reply:', error);
      toast.error("Failed to send message");
    } finally {
      setIsSendingReply(false);
    }
  };

  // Fetch changelog articles from Supabase
  const { data: changelogArticles, isLoading: articlesLoading } = useQuery({
    queryKey: ['changelog-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('help_articles')
        .select(`
          id,
          title,
          slug,
          created_at,
          help_collections!inner(slug)
        `)
        .eq('help_collections.slug', 'changelog')
        .eq('published', true)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch popular articles from Supabase
  const { data: popularArticles, isLoading: popularLoading } = useQuery({
    queryKey: ['popular-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('help_articles')
        .select(`
          id,
          title,
          slug,
          help_collections(slug)
        `)
        .eq('is_popular', true)
        .eq('published', true)
        .limit(5);
      
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const userName = user?.user_metadata?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || "there";
  const userInitial = userName.charAt(0).toUpperCase();

  const getStatusColor = () => {
    if (statusLoading || isError) return "text-muted-foreground";
    const indicator = statusData?.overall_status?.indicator;
    if (indicator === "none") return "text-green-500";
    if (indicator === "minor") return "text-yellow-500";
    if (indicator === "major") return "text-orange-500";
    if (indicator === "critical") return "text-red-500";
    return "text-green-500";
  };

  const getStatusBgColor = () => {
    if (statusLoading || isError) return "bg-muted/10";
    const indicator = statusData?.overall_status?.indicator;
    if (indicator === "none") return "bg-green-500/10";
    if (indicator === "minor") return "bg-yellow-500/10";
    if (indicator === "major") return "bg-orange-500/10";
    if (indicator === "critical") return "bg-red-500/10";
    return "bg-green-500/10";
  };

  const getStatusText = () => {
    if (statusLoading) return "Checking status...";
    if (isError) return "Unable to fetch status";
    return statusData?.overall_status?.description || "All Systems Operational";
  };

  const getStatusIcon = () => {
    if (statusLoading) return <Loader2 className="w-5 h-5 animate-spin" />;
    if (isError) return <AlertCircle className="w-5 h-5" />;
    return <CheckCircle className="w-5 h-5" />;
  };
  // Messenger content shared between mobile and desktop
  const messengerContent = (
    <div className={cn(
      "flex flex-col overflow-hidden",
      isMobile ? "h-full" : "h-[600px] max-h-[calc(100vh-120px)]"
    )}>
          {/* Header with gradient - only show on home tab */}
          {activeTab === "home" && (
            <div className={cn(
              "bg-gradient-to-br from-primary via-primary/90 to-primary/70 p-6 pb-8 relative z-10 shrink-0",
              isMobile && "pt-12"
            )}>
              <div className="flex items-center justify-between mb-8">
                <span className="text-primary-foreground/90 font-medium text-sm">queueOS</span>
                {user && (
                  <Avatar className="h-10 w-10 border-2 border-primary-foreground/20">
                    <AvatarImage src={avatarUrl || undefined} alt="Profile" />
                    <AvatarFallback className="bg-primary-foreground/20 text-primary-foreground font-medium">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
              <div>
                <h2 className="text-primary-foreground/80 text-2xl font-light">
                  Hi {userName} 👋
                </h2>
                <h3 className="text-primary-foreground text-2xl font-semibold">
                  How can we help?
                </h3>
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className={cn(
            "flex-1 bg-background overflow-y-auto",
            isMobile && activeTab !== "home" && "pt-12"
          )}>
            {activeTab === "home" && (
              <div className="p-4 space-y-4">
                {/* Recent Messages Card - Only show when logged in */}
                {user && (
                  <div className="bg-card border border-border rounded-xl p-4">
                    <h4 className="text-foreground font-semibold mb-2">Recent messages</h4>
                    {hasPaidPlan && userTickets && userTickets.length > 0 ? (
                      <button
                        onClick={() => {
                          setSelectedTicket(userTickets[0]);
                          setActiveTab("tickets");
                        }}
                        className="w-full text-left p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors mb-3 relative"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">#{userTickets[0].ticket_number}</span>
                          <div className="flex items-center gap-2">
                            {unreadCounts?.[userTickets[0].id] && unreadCounts[userTickets[0].id] > 0 && (
                              <span className="min-w-5 h-5 px-1.5 bg-destructive text-destructive-foreground text-xs font-bold rounded-full flex items-center justify-center">
                                {unreadCounts[userTickets[0].id]}
                              </span>
                            )}
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full",
                              userTickets[0].status === 'closed' ? "bg-muted text-muted-foreground" :
                              userTickets[0].status === 'resolved' ? "bg-green-500/10 text-green-500" :
                              "bg-primary/10 text-primary"
                            )}>
                              {formatTicketStatus(userTickets[0].status)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm font-medium text-foreground truncate">{userTickets[0].subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(userTickets[0].created_at).toLocaleDateString()}
                        </p>
                      </button>
                    ) : (
                      <p className="text-muted-foreground text-sm mb-3">
                        No recent messages
                      </p>
                    )}
                    <Button 
                      className="w-full"
                      onClick={() => setActiveTab("tickets")}
                    >
                      {hasPaidPlan && userTickets && userTickets.length > 0 ? "View All Tickets" : "Submit a Ticket"}
                    </Button>
                  </div>
                )}

                {/* Status Card */}
                <a 
                  href="https://status.queueos.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors"
                >
                  <div className={cn("p-2 rounded-full", getStatusBgColor(), getStatusColor())}>
                    {getStatusIcon()}
                  </div>
                  <div className="flex-1">
                    <p className={cn("font-medium text-sm", getStatusColor())}>
                      {getStatusText()}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      View status page
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </a>

                {/* Popular Articles - Only show when logged in */}
                {user && (
                  <div className="bg-card border border-border rounded-xl p-4">
                    <h4 className="text-foreground font-semibold mb-3">Popular Articles</h4>
                    {popularLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : popularArticles && popularArticles.length > 0 ? (
                      <div className="space-y-2">
                        {popularArticles.map((article) => (
                          <a
                            key={article.id}
                            href={`https://help.queueos.com/collection/${article.help_collections?.slug || 'articles'}/${article.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ExternalLink className="w-3 h-3 shrink-0" />
                            <span className="truncate">{article.title}</span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">No articles available</p>
                    )}
                  </div>
                )}

                {/* Get a Demo Card - Only show when logged out */}
                {!user && (
                  <div className="bg-card border border-border rounded-xl p-4">
                    <h4 className="text-foreground font-semibold mb-2">Get a demo</h4>
                    <p className="text-muted-foreground text-sm mb-4">
                      Get a look inside queueOS to discover everything it can do for you.
                    </p>
                    <Button 
                      className="w-full"
                      onClick={() => window.open('https://calendar.app.google/YYAFgz6B99iURmJi6', '_blank')}
                    >
                      Request a demo
                    </Button>
                  </div>
                )}

                {/* Help Center Link */}
                <a 
                  href="https://help.queueos.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-card border border-border rounded-xl p-4 hover:bg-muted/50 transition-colors"
                >
                  <div>
                    <h4 className="text-foreground font-semibold">Help Center</h4>
                    <p className="text-muted-foreground text-sm">Search our knowledge base</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-muted-foreground" />
                </a>
              </div>
            )}

            {activeTab === "tickets" && (
              <div className="p-4 h-full flex flex-col">
                {showNewTicketForm ? (
                  // New Ticket Form
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1 h-8 w-8"
                        onClick={() => setShowNewTicketForm(false)}
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                      <h4 className="text-foreground font-semibold">New Ticket</h4>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="ticket-subject">Subject *</Label>
                        <Input
                          id="ticket-subject"
                          placeholder="Brief description of your issue"
                          value={ticketSubject}
                          onChange={(e) => setTicketSubject(e.target.value)}
                          maxLength={200}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="ticket-category">Category</Label>
                        <Select value={ticketCategory} onValueChange={setTicketCategory}>
                          <SelectTrigger id="ticket-category">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            {ticketCategories?.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="ticket-priority">Priority</Label>
                        <Select value={ticketPriority} onValueChange={setTicketPriority}>
                          <SelectTrigger id="ticket-priority">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="ticket-message">Message *</Label>
                        <Textarea
                          id="ticket-message"
                          placeholder="Describe your issue in detail..."
                          value={ticketMessage}
                          onChange={(e) => setTicketMessage(e.target.value)}
                          rows={5}
                          maxLength={2000}
                        />
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full"
                      onClick={handleSubmitTicket}
                      disabled={isSubmitting || !ticketSubject.trim() || !ticketMessage.trim()}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Submit Ticket
                    </Button>
                  </div>
                ) : selectedTicket ? (
                  // Conversation View
                  <div className="flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1 h-8 w-8"
                        onClick={() => setSelectedTicket(null)}
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-foreground font-semibold text-sm truncate">
                          {selectedTicket.subject}
                        </h4>
                        <span className="text-xs text-muted-foreground">
                          #{selectedTicket.ticket_number}
                        </span>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full shrink-0",
                        selectedTicket.status === 'submitted' && "bg-blue-500/10 text-blue-500",
                        selectedTicket.status === 'in_progress' && "bg-yellow-500/10 text-yellow-500",
                        selectedTicket.status === 'waiting_customer' && "bg-orange-500/10 text-orange-500",
                        selectedTicket.status === 'closed' && "bg-muted text-muted-foreground"
                      )}>
                        {formatTicketStatus(selectedTicket.status)}
                      </span>
                    </div>
                    
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto space-y-3 mb-4 min-h-0">
                      {messagesLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : ticketMessages && ticketMessages.length > 0 ? (
                        ticketMessages.map((msg) => (
                          msg.sender_type === 'system' ? (
                            // System message - centered notification style
                            <div
                              key={msg.id}
                              className="flex justify-center my-2"
                            >
                              <div className="bg-muted/50 text-muted-foreground text-xs px-3 py-1.5 rounded-full text-center max-w-[90%]">
                                <p>{msg.message}</p>
                                <p className="text-[10px] mt-0.5 opacity-70">
                                  {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                                </p>
                              </div>
                            </div>
                          ) : (
                            // Customer or staff message
                            <div
                              key={msg.id}
                              className={cn(
                                "p-3 rounded-lg max-w-[85%]",
                                msg.sender_type === 'customer'
                                  ? "bg-primary text-primary-foreground ml-auto"
                                  : "bg-muted mr-auto"
                              )}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                              <div className={cn(
                                "text-xs mt-1",
                                msg.sender_type === 'customer' 
                                  ? "text-primary-foreground/70" 
                                  : "text-muted-foreground"
                              )}>
                                <p>{format(new Date(msg.created_at), 'MMM d, h:mm a')}</p>
                                {msg.sender_type === 'staff' && msg.sender_name && (
                                  <p className="font-medium">{msg.sender_name}</p>
                                )}
                              </div>
                            </div>
                          )
                        ))
                      ) : (
                        <p className="text-center text-muted-foreground text-sm py-4">
                          No messages yet
                        </p>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    
                    {/* Reply Input */}
                    {selectedTicket.status !== 'closed' && (
                      <div className="flex gap-2 mt-auto pt-2 border-t border-border">
                        <Input
                          placeholder="Type your message..."
                          value={replyMessage}
                          onChange={(e) => handleReplyChange(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendReply();
                            }
                          }}
                          disabled={isSendingReply}
                        />
                        <Button 
                          size="icon"
                          onClick={handleSendReply}
                          disabled={isSendingReply || !replyMessage.trim()}
                        >
                          {isSendingReply ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : !user ? (
                  // Not logged in
                  <div className="text-center py-12">
                    <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h4 className="text-foreground font-semibold mb-2">No tickets available</h4>
                    <p className="text-muted-foreground text-sm mb-4">
                      Sign in to view your tickets or contact our support team
                    </p>
                    <Button onClick={() => window.open('https://help.queueos.com/support', '_blank')}>
                      Contact Support
                    </Button>
                  </div>
                ) : subscriptionLoading ? (
                  // Loading subscription
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : !subscriptionData?.subscribed ? (
                  // Free plan - tickets not available
                  <div className="text-center py-12">
                    <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h4 className="text-foreground font-semibold mb-2">Tickets for Paid Plans</h4>
                    <p className="text-muted-foreground text-sm mb-4">
                      Ticket requests are available for Starter and Professional plans
                    </p>
                    <Button onClick={() => window.open('https://help.queueos.com/support', '_blank')}>
                      Contact Support
                    </Button>
                  </div>
                ) : ticketsLoading ? (
                  // Loading tickets
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : userTickets && userTickets.length > 0 ? (
                  // Display user tickets
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-foreground font-semibold">Your Tickets</h4>
                      <Button 
                        size="sm"
                        onClick={() => setShowNewTicketForm(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        New Ticket
                      </Button>
                    </div>
                    {userTickets.map((ticket) => (
                      <button
                        key={ticket.id}
                        onClick={() => setSelectedTicket({
                          id: ticket.id,
                          ticket_number: ticket.ticket_number,
                          subject: ticket.subject,
                          status: ticket.status || 'submitted'
                        })}
                        className="w-full text-left bg-card border border-border rounded-xl p-4 hover:bg-muted/50 transition-colors cursor-pointer relative"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-xs text-muted-foreground font-mono">
                            #{ticket.ticket_number}
                          </span>
                          <div className="flex items-center gap-2">
                            {unreadCounts?.[ticket.id] && unreadCounts[ticket.id] > 0 && (
                              <span className="min-w-5 h-5 px-1.5 bg-destructive text-destructive-foreground text-xs font-bold rounded-full flex items-center justify-center">
                                {unreadCounts[ticket.id]}
                              </span>
                            )}
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full",
                              ticket.status === 'submitted' && "bg-blue-500/10 text-blue-500",
                              ticket.status === 'in_progress' && "bg-yellow-500/10 text-yellow-500",
                              ticket.status === 'waiting_customer' && "bg-orange-500/10 text-orange-500",
                              ticket.status === 'closed' && "bg-muted text-muted-foreground"
                            )}>
                              {formatTicketStatus(ticket.status)}
                            </span>
                          </div>
                        </div>
                        <h5 className="text-foreground font-medium text-sm line-clamp-2">
                          {ticket.subject}
                        </h5>
                        <p className="text-muted-foreground text-xs mt-2">
                          {format(new Date(ticket.created_at), 'MMM d, yyyy')}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : (
                  // No tickets yet (paid user)
                  <div className="text-center py-12">
                    <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h4 className="text-foreground font-semibold mb-2">No tickets yet</h4>
                    <p className="text-muted-foreground text-sm mb-4">
                      Submit a ticket to our support team
                    </p>
                    <Button onClick={() => setShowNewTicketForm(true)}>
                      Submit a Ticket
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "news" && (
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-foreground font-semibold">Latest Updates</h4>
                  <a 
                    href="https://help.queueos.com/collection/changelog" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary text-sm hover:underline"
                  >
                    View all
                  </a>
                </div>
                
                {/* Changelog Articles */}
                {articlesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : changelogArticles && changelogArticles.length > 0 ? (
                  changelogArticles.map((article) => (
                    <a
                      key={article.id}
                      href={`https://help.queueos.com/collection/changelog/${article.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-card border border-border rounded-xl p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h5 className="text-foreground font-medium text-sm">{article.title}</h5>
                        </div>
                        <span className="text-muted-foreground text-xs whitespace-nowrap">
                          {format(new Date(article.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </a>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Megaphone className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">No updates available</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom Navigation */}
          <div className="bg-card border-t border-border p-2 flex justify-around shrink-0">
            <button
              onClick={() => setActiveTab("home")}
              className={cn(
                "flex flex-col items-center gap-1 px-6 py-2 rounded-lg transition-colors",
                activeTab === "home" ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Home className="w-5 h-5" />
              <span className="text-xs font-medium">Home</span>
            </button>
            <button
              onClick={() => setActiveTab("tickets")}
              className={cn(
                "flex flex-col items-center gap-1 px-6 py-2 rounded-lg transition-colors relative",
                activeTab === "tickets" ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className="relative">
                <Ticket className="w-5 h-5" />
                {totalUnreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </span>
                )}
              </div>
              <span className="text-xs font-medium">Tickets</span>
            </button>
            <button
              onClick={() => setActiveTab("news")}
              className={cn(
                "flex flex-col items-center gap-1 px-6 py-2 rounded-lg transition-colors",
                activeTab === "news" ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Megaphone className="w-5 h-5" />
              <span className="text-xs font-medium">News</span>
            </button>
          </div>
        </div>
  );

  // Don't show messenger on public form pages or AI page
  if (isPublicFormPage || isAIPage) {
    return null;
  }

  return (
    <>
      {/* Floating Button - Hidden on mobile */}
      {!isMobile && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center",
            "bg-primary hover:bg-primary/90 text-primary-foreground",
            "hover:scale-105 active:scale-95",
            isOpen && "rotate-90"
          )}
          aria-label={isOpen ? "Close messenger" : "Open messenger"}
        >
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <MessageCircle className="w-6 h-6" />
          )}
          {/* Unread notification bubble on launcher */}
          {!isOpen && totalUnreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1.5 bg-destructive text-destructive-foreground text-xs font-bold rounded-full flex items-center justify-center">
              {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
            </span>
          )}
        </button>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerContent className="h-[100dvh] max-h-[100dvh] rounded-t-none [&>div:first-child]:hidden">
            {/* Mobile X close button */}
            <button
              onClick={() => setIsOpen(false)}
              className={cn(
                "absolute top-4 left-4 z-50 w-8 h-8 rounded-full flex items-center justify-center transition-colors",
                activeTab === "home" 
                  ? "bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30" 
                  : "bg-muted text-foreground hover:bg-muted/80"
              )}
              aria-label="Close messenger"
            >
              <X className="w-5 h-5" />
            </button>
            {messengerContent}
          </DrawerContent>
        </Drawer>
      )}

      {/* Desktop Panel */}
      {!isMobile && isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 fade-in duration-300">
          {messengerContent}
        </div>
      )}
    </>
  );
};
